'use client';

import { Check, Copy, Tag } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { LAUNCH_PROMO } from '@/configs/promo';

export const PROMO_BANNER_HEIGHT_PX = 52;

export function PromoBanner() {
  const t = useTranslations('PromoBanner');
  const bannerRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useLayoutEffect(() => {
    const syncHeight = () => {
      const height = bannerRef.current?.offsetHeight ?? PROMO_BANNER_HEIGHT_PX;
      const navBlockHeight = window.innerWidth >= 768 ? 77 : 57;

      document.documentElement.style.setProperty(
        '--promo-banner-height',
        `${height}px`
      );
      document.documentElement.style.setProperty(
        '--landing-header-bottom',
        `${height + navBlockHeight}px`
      );
    };

    syncHeight();
    window.addEventListener('resize', syncHeight);

    return () => {
      window.removeEventListener('resize', syncHeight);
      document.documentElement.style.removeProperty('--promo-banner-height');
      document.documentElement.style.removeProperty('--landing-header-bottom');
    };
  }, []);

  const copyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(LAUNCH_PROMO.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, []);

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label={t('ariaLabel')}
      className="bg-surface-subtle border-border-soft text-text-primary fixed inset-x-0 top-0 z-[60] border-b"
    >
      <div className="mx-auto flex max-w-[1520px] items-center justify-center px-3 py-2 sm:px-4 sm:py-2.5">
        <div className="flex max-w-full flex-nowrap items-center justify-center gap-2 sm:gap-2.5">
          <span className="font-matter inline-flex min-w-0 shrink items-center gap-1.5 text-sm leading-tight font-medium sm:gap-2 sm:text-base">
            <Tag
              size={16}
              className="text-brand-primary hidden shrink-0 sm:block"
              aria-hidden
            />
            <span className="sm:hidden">{t('messageShort')}</span>
            <span className="hidden sm:inline">{t('message')}</span>
          </span>
          <div
            role="button"
            tabIndex={0}
            onClick={copyCode}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                void copyCode();
              }
            }}
            className="font-matter isolation-isolate relative inline-flex w-fit shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-[100px] border border-solid px-3 py-0.5 sm:gap-2 sm:px-3.5 sm:py-1"
            style={{
              backgroundColor: 'var(--promo-chip-bg)',
              borderColor: 'var(--promo-chip-ring)',
              boxShadow: 'var(--promo-chip-shadow)',
            }}
            aria-label={t('copyAriaLabel', { code: LAUNCH_PROMO.code })}
          >
            <span className="text-brand-primary text-sm font-semibold tracking-wide sm:text-base">
              {LAUNCH_PROMO.code}
            </span>
            {copied ? (
              <Check size={16} className="text-brand-primary" aria-hidden />
            ) : (
              <Copy
                size={16}
                className="text-brand-secondary dark:text-text-secondary"
                aria-hidden
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
